// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { PaperProps } from '@mantine/core';
import { Badge, Divider, Flex, Group, Paper, Stack, Text } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import type { Patient, Practitioner, Reference, ResourceType, Task } from '@medplum/fhirtypes';
import { CodeInput, DateTimeInput, ReferenceInput, ResourceInput } from '@medplum/react';
import { getDmeTaskDueDate } from '@polarissleep/shared';
import React, { useEffect, useState } from 'react';
import { isDmeSubmissionTask } from '../../utils/dme-task';
import { ProviderSelect } from './ProviderSelect';

interface TaskPropertiesProps extends PaperProps {
  task: Task;
  onTaskChange: (task: Task) => void;
}

export function TaskProperties(props: TaskPropertiesProps): React.JSX.Element {
  const { task: initialTask, onTaskChange, ...paperProps } = props;
  const [task, setTask] = useState<Task | undefined>(initialTask);
  const [dueDate, setDueDate] = useState<string | undefined>(task?.restriction?.period?.end);
  const isDmeTask = isDmeSubmissionTask(task);

  useEffect(() => {
    setTask(initialTask);
  }, [initialTask]);

  const handleDueDateChange = async (value: string | undefined): Promise<void> => {
    setDueDate(value);
    await handleTaskUpdate({ ...task, restriction: { ...task?.restriction, period: { end: value } } } as Task);
  };

  const handlePatientChange = async (value: Reference<Patient> | undefined): Promise<void> => {
    await handleTaskUpdate({ ...task, for: value } as Task);
  };

  const handlePriorityChange = async (value: string): Promise<void> => {
    await handleTaskUpdate({
      ...task,
      priority: value as Task['priority'],
      ...(isDmeTask ? { restriction: { period: { end: getDmeTaskDueDate(value) } } } : {}),
    } as Task);
  };

  const handleOwnerChange = async (value: Reference<Practitioner> | undefined): Promise<void> => {
    await handleTaskUpdate({ ...task, owner: value } as Task);
  };

  const handleStatusChange = async (value: string | undefined): Promise<void> => {
    if (value) {
      await handleTaskUpdate({ ...task, status: value as Task['status'] } as Task);
    }
  };

  const handleTaskUpdate = async (value: Task): Promise<void> => {
    onTaskChange(value);
  };

  return (
    <Paper {...paperProps}>
      <Flex direction="column" gap="lg">
        <Stack gap="xs">
          {!isDmeTask && (
            <CodeInput
              key={`${task?.status}-${task?.id}`}
              name="status"
              label="Status"
              binding="http://hl7.org/fhir/ValueSet/task-status"
              maxValues={1}
              defaultValue={task?.status}
              onChange={handleStatusChange}
            />
          )}

          {isDmeTask ? (
            <Text size="sm" c="dimmed">
              Due date: {dueDate ? new Date(dueDate).toLocaleString() : 'SLA-derived'} (priority SLA)
            </Text>
          ) : (
            <DateTimeInput
              name="Due Date"
              placeholder="End"
              label="Due Date"
              defaultValue={dueDate}
              onChange={handleDueDateChange}
            />
          )}

          <Stack gap={0}>
            <Text size="sm" fw={500}>
              Assignee
            </Text>
            <ProviderSelect name="owner" aria-label="Assignee" value={task?.owner} onChange={handleOwnerChange} />
          </Stack>

          <CodeInput
            name="priority"
            label="Priority"
            binding="http://hl7.org/fhir/ValueSet/request-priority"
            maxValues={1}
            defaultValue={task?.priority?.toString()}
            onChange={(value: string | undefined) => handlePriorityChange(value ?? '')}
          />

          {task?.basedOn && task.basedOn.length > 0 ? (
            <ResourceInput
              label="Based On"
              resourceType={task.basedOn[0].reference?.split('/').pop() as ResourceType}
              name="basedOn-0"
              defaultValue={task.basedOn[0]}
              disabled
            />
          ) : (
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                Based On
              </Text>
              <ReferenceInput
                name="basedOn"
                placeholder="Select any resource..."
                onChange={async (value: Reference | undefined) => {
                  if (value?.reference) {
                    const newBasedOn = [...(task?.basedOn || []), value];
                    await handleTaskUpdate({ ...task, basedOn: newBasedOn } as Task);
                  }
                }}
              />
            </Stack>
          )}
        </Stack>

        <Divider />

        <Stack gap="xs" pt="md">
          <ResourceInput
            label="Patient"
            resourceType="Patient"
            name="patient"
            placeholder="Search for patient"
            defaultValue={task?.for as Reference<Patient>}
            onChange={async (patient: Patient | undefined) => {
              await handlePatientChange(patient ? { reference: getReferenceString(patient) } : undefined);
            }}
          />

          {task?.encounter && (
            <ResourceInput label="Encounter" resourceType="Encounter" name="encounter" defaultValue={task.encounter} />
          )}
        </Stack>

        <WorkflowContext task={task} />
      </Flex>
    </Paper>
  );
}

function WorkflowContext({ task }: { task: Task | undefined }): React.JSX.Element | null {
  const actionId = task?.extension?.find((e) => e.url === 'http://polarissleep.com/workflow-action-id')?.valueString;

  const dependencies = (task?.extension ?? [])
    .filter((e) => e.url === 'http://polarissleep.com/workflow-depends-on')
    .map((e) => e.valueString)
    .filter(Boolean);

  const conditions = (task?.extension ?? [])
    .filter((e) => e.url === 'http://polarissleep.com/workflow-condition')
    .map((e) => e.valueString)
    .filter(Boolean);

  if (!actionId) {
    return null;
  }

  return (
    <>
      <Divider />
      <Stack gap="xs" pt="md">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          Workflow
        </Text>
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            Action ID
          </Text>
          <Text size="sm" c="dimmed" ff="monospace">
            {actionId}
          </Text>
        </Stack>
        {dependencies.length > 0 && (
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              Depends On
            </Text>
            <Group gap={4}>
              {dependencies.map((dep) => (
                <Badge key={dep} size="xs" variant="outline" color="gray">
                  {dep}
                </Badge>
              ))}
            </Group>
          </Stack>
        )}
        {conditions.length > 0 && (
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              Conditions
            </Text>
            <Group gap={4}>
              {conditions.map((cond) => (
                <Badge key={cond} size="xs" variant="outline" color="orange">
                  {cond}
                </Badge>
              ))}
            </Group>
          </Stack>
        )}
      </Stack>
    </>
  );
}
